import React, { FC, useRef, useState } from 'react';
import FileUpload from '../../components/FileUpload';
import Button from '../../components/GroupActions/Button';
import downloadObjectAsJson, { downloadObjectAsSvg } from '../../utils/download';
import ColorRampForm from '../../components/ColorRampForm';
import { nanoid } from 'nanoid';
import { read } from '../../utils/download';
import prism_colorRamps from '../../data/prism_colorRamps.json';
import PrismColorDropdown from '../../components/PrismColorDropdown';

export interface SingleProps {}

export const Single: FC<SingleProps> = (props) => {
  const [ramps, setRamps] = useState<any[]>([]); // formSchema

  const rampsRef = useRef([]);

  const elements = ramps.map((ramp, index) => {
    // @ts-ignore

    const innerRef = (el) => (rampsRef.current[index] = el);

    const props = {
      ...ramp.props,
      innerRef: innerRef,
      position: index,
      handleAdd: addElement,
      handleRemove: removeElement,
    };
    const single = React.createElement(ColorRampForm, props, []);
    if (single) {
      return { id: ramp.props.id, component: <React.Fragment key={ramp.props.id}>{single}</React.Fragment> };
    }
    return null;
  });

  // @TODO: make this more global
  function addElement(pos?: number) {
    const id = nanoid();
    const newElement = {
      ColorRampForm,
      id,
      props: {
        defaultName: `Ramp - ${id}`,
        id,
      },
      children: [],
    };
    if (pos) {
      const newSchema = [...ramps.slice(0, pos), newElement, ...ramps.slice(pos)];

      setRamps(newSchema);
    } else {
      setRamps(ramps.concat([newElement]));
    }
  }

  function removeElement(pos: number) {
    const newSchema = [...ramps.slice(0, pos), ...ramps.slice(pos + 1)];
    setRamps(newSchema);
  }

  const handleDownload = (type = 'json') => {
    const data = rampsRef.current
      .map((group) => {
        // @ts-ignore
        return group ? group.values : null;
      })
      .filter((item) => item);

    if (type === 'svg') {
      const groupCount = data.length;
      const swatchCount = 10;
      const viewBox = `0 0 ${swatchCount * 70 + (swatchCount - 1) * 9} ${groupCount * 70 + (groupCount - 1) * 48}`;
      let svgOpen = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}">`;
      let svgClose = '</svg>';
      let y_position = 0;
      const shapes = data.reduce((acc, curr, idx) => {
        y_position = y_position + Math.min(idx, 1) * 118;
        let x_position = 0;
        const rectSet = curr.colors
          .reduce((a: any, c: any, i: number) => {
            x_position = x_position + Math.min(i, 1) * 79;

            const rect = `<rect data-name="${
              i + 1
            }0" width="70" height="70" x="${x_position}" y="${y_position}" style="fill: ${c.hex}"/>`;
            return a.concat(rect);
          }, [])
          .join('');
        return (acc += `<g data-name="${curr.name}">${rectSet}</g>`);
      }, '');
      downloadObjectAsSvg(`${svgOpen}${shapes}${svgClose}`, 'ramps');
    }

    if (type === 'json') {
      data.reduce((acc, curr) => {
        return {
          ...acc,
          [curr.name]: curr.colors.reduce((a: any, c: any, i: number) => {
            let id = `_${i}0`;
            if (i === 0) {
              id = '_white';
            }
            if (curr.colors.length > 1 && i === curr.colors.length - 1) {
              id = '_black';
            }
            return { ...a, [`${id}`]: c };
          }, {}),
        };
      }, {});
      downloadObjectAsJson(data, 'color_ramps');
    }
  };

  return (
    <div>
      <h1>Color Ramps</h1>
      <p>
        Begin by importing some ramps or starting from scratch. Ramps can be downloaded as a JSON file by clicking the
        "Download" button at the bottom of the page (once ramps exist.)
      </p>
      <h4>1. Import a previously downloaded ramp set.</h4>
      <FileUpload
        onFileSelectSuccess={(file: any) => {
          read(file).then((res) => {
            const prefill = Object.entries(res).map(([rampName, rampValue]) => ({
              id: rampName,
              props: {
                id: rampName,
                defaultName: rampName,
                initialValues: rampValue,
              },
              children: [],
            }));
            setRamps(ramps.concat(prefill));
          });
        }}
      />
      <h4>2. Import existing color ramps.</h4>
      <PrismColorDropdown
        items={prism_colorRamps}
        fn={(filteredItems) => {
          const imported = filteredItems.map((item) => ({
            id: item,
            props: {
              defaultName: item,
              id: item,
              // @ts-ignore
              initialValues: prism_colorRamps[item],
            },
            children: [],
          }));

          setRamps(ramps.concat(imported));
        }}
      />

      {ramps.length < 1 && (
        <>
          <h4>3. Build a custom ramp from a base color.</h4>
          <div style={{ border: '2px dashed #a8a8a8', padding: '12px', borderRadius: '4px' }}>
            <Button label='Add a Ramp' onClick={() => addElement()} theme='accent' variant='rounded' />
          </div>
        </>
      )}

      {elements.map((el, index) => (
        <React.Fragment key={el?.id || index}>{el?.component}</React.Fragment>
      ))}

      {ramps.length > 0 && (
        <>
          <Button
            onClick={() => handleDownload('svg')}
            label='Download Ramps as SVG'
            theme='accent'
            variant='rounded'
          />
          <Button
            onClick={() => handleDownload('json')}
            label='Download Ramps as JSON'
            theme='accent'
            variant='outline'
          />
        </>
      )}
    </div>
  );
};
export default Single;
